import { useMutation, useQueryClient } from '@tanstack/react-query';
import Api, { withAcl } from 'hooks/Api';
import { useQuickPaginationContext } from 'ui/components/nav/Pagination/QuickPagination';
import { toastSuccess } from 'ui/partials/Toast';
import { useAdminParams } from 'utils/helpers';
import { useProvider } from 'utils/providers/Provider';
// import { DEAL_FORM } from '../forms/schema';
import { Deal } from 'utils/types';
import { useTour } from '@reactour/tour';
import partialConstants from 'ui/partials/utils/constants';
import useModal from 'ui/partials/Modal/useModal';

import contactsConstants from '../../contact/utils/constants';
import c from '../utils/constants';

interface Payload {
  id: string;
  isFullUpdate?: boolean;
  stageId?: string;
  pipelineId?: string;
  amount?: number;
  tagId?: string[];
}

export default function useUpdateDeal() {
  const { setStageId } = useQuickPaginationContext() ?? {};
  const { hideModal } = useModal();
  const queryClient = useQueryClient();
  const { state } = useProvider();
  const { accountId, accountUserId } = useAdminParams();
  const { isOpen, setIsOpen } = useTour();

  const args = state;

  return useMutation({
    mutationFn: async (payload: Payload) => {
      // Triggers a successful mutation without writing to the DB
      // Only used when a tour is opened
      if (isOpen) {
        setIsOpen(false);
        return { data: {} };
      }

      const api = new Api(args);

      const data = await api.patch(
        await withAcl({
          account: {
            url: `/api/v1/sales/deals/${payload.id}`,
            payload,
          },
          admin: {
            url: `/api/v2/accounts/${accountId}/users/${accountUserId}/sales/deals/${payload.id}`,
            payload,
          },
        }),
      );

      return data;
    },
    onSuccess: (successData: { data: Deal }, payload) => {
      setStageId?.(successData?.data?.stageId as string);

      queryClient.invalidateQueries([
        c.PIPELINES,
        successData?.data.pipelineId,
        c.DEALS,
      ]);

      if (payload.isFullUpdate) {
        queryClient.invalidateQueries({
          queryKey: [c.PIPELINES, c.DEALS, payload.id],
          exact: true,
        });
      }

      queryClient.invalidateQueries([c.LOGS, successData?.data?._id]);
      queryClient.invalidateQueries([c.DEALS, { keyword: '' }]);
      queryClient.invalidateQueries({
        queryKey: [contactsConstants.LEADS],
      });
      queryClient.invalidateQueries({
        queryKey: [contactsConstants.COMPANIES],
      });
      queryClient.invalidateQueries({
        queryKey: [partialConstants.TAGS],
      });

      hideModal();
      toastSuccess('Successfully updated deal.');
    },
  });
}
